import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useContent } from '@/context/ContentContext';
import { serviceCategories } from '@/data/content';

type WindowKind = 'working' | 'break' | 'other';
type Window = { weekday: number; kind: WindowKind; start: string; end: string; note?: string };

type Override = {
  shopify_product_id: string; // reused as service slug id
  bookable: boolean;
  duration_minutes: number;
  allowed_weekdays: number[] | null;
  allowed_windows: Window[] | null;
  buffer_minutes: number | null;
  slot_step_minutes: number | null;
  min_lead_minutes: number | null;
};

type GlobalDefaults = {
  buffer_minutes: number;
  slot_step_minutes: number;
  min_lead_minutes: number;
};

const KIND_LABEL: Record<WindowKind, string> = {
  working: 'שעות עבודה',
  break: 'הפסקה',
  other: 'אחר',
};

const KIND_STYLE: Record<WindowKind, string> = {
  working: 'bg-emerald-50 border-emerald-200',
  break: 'bg-amber-50 border-amber-200',
  other: 'bg-neutral-50 border-neutral-200',
};

const DAYS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

// Pulled from the single source of truth used by the Appointment widget and
// the Shopify item editor — so admin labels match the public site exactly.
const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  serviceCategories.map((c) => [c.id, c.label]),
);

function parseDurationMin(d?: string): number {
  if (!d) return 60;
  const m = d.match(/\d+/);
  return m ? Number(m[0]) : 60;
}

type ServiceMeta = {
  id: string;
  title: string;
  subtitle?: string;
  category?: string;
  price?: string;
  duration?: string;
};

function defaultOverride(s: ServiceMeta): Override {
  return {
    shopify_product_id: s.id,
    bookable: true,
    duration_minutes: parseDurationMin(s.duration),
    allowed_weekdays: null,
    allowed_windows: null,
    buffer_minutes: null,
    slot_step_minutes: null,
    min_lead_minutes: null,
  };
}

export default function AdminServices() {
  const [overrides, setOverrides] = useState<Record<string, Override>>({});
  const [defaults, setDefaults] = useState<GlobalDefaults>({
    buffer_minutes: 10,
    slot_step_minutes: 15,
    min_lead_minutes: 120,
  });

  // Use merged services (local + Shopify productType/data) so categories match
  // what's set in Shopify and on the public site.
  const { services: mergedServices } = useContent();
  const services: ServiceMeta[] = useMemo(
    () =>
      (mergedServices as any[]).map((s) => ({
        id: String(s.id),
        title: s.title as string,
        subtitle: s.subtitle as string | undefined,
        category: s.category as string | undefined,
        price: s.price as string | undefined,
        duration: s.duration as string | undefined,
      })),
    [mergedServices],
  );

  const load = async () => {
    const [{ data: ovr }, { data: sched }] = await Promise.all([
      supabase.from('service_overrides').select('*'),
      supabase
        .from('clinic_schedule')
        .select('buffer_minutes,slot_step_minutes,min_lead_minutes')
        .limit(1)
        .maybeSingle(),
    ]);
    const map: Record<string, Override> = {};
    (ovr as any[] | null)?.forEach((o) => {
      map[o.shopify_product_id] = o;
    });
    setOverrides(map);
    if (sched) {
      setDefaults({
        buffer_minutes: sched.buffer_minutes,
        slot_step_minutes: sched.slot_step_minutes,
        min_lead_minutes: sched.min_lead_minutes,
      });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveOverride = async (pid: string, next: Override) => {
    const { error } = await supabase.from('service_overrides').upsert(next as any);
    if (error) {
      toast.error(error.message);
      return false;
    }
    setOverrides((m) => ({ ...m, [pid]: next }));
    return true;
  };

  const grouped = useMemo(() => {
    const map = new Map<string, ServiceMeta[]>();
    services.forEach((s) => {
      const key = s.category ?? 'other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    });
    return Array.from(map.entries());
  }, [services]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">טיפולים</h1>
        <Badge variant="secondary">{services.length} טיפולים</Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        רשימת הטיפולים נטענת מהקטלוג של האתר. הגדר/י כאן את כללי ההזמנה — הם יחולו על מערכת ההזמנות באתר.
      </p>

      {grouped.map(([cat, list]) => (
        <div key={cat} className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {CATEGORY_LABELS[cat] ?? cat}
          </h2>
          {list.map((s) => (
            <ServiceRow
              key={s.id}
              service={s}
              override={overrides[s.id]}
              defaults={defaults}
              onSave={(next) => saveOverride(s.id, next)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function ServiceRow({
  service,
  override,
  defaults,
  onSave,
}: {
  service: ServiceMeta;
  override: Override | undefined;
  defaults: GlobalDefaults;
  onSave: (next: Override) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Override>(override ?? defaultOverride(service));
  const [saving, setSaving] = useState(false);

  // Re-sync draft from props when override updates externally (e.g. after save).
  useEffect(() => {
    if (!editing) setDraft(override ?? defaultOverride(service));
  }, [override, service, editing]);

  const handleSave = async () => {
    setSaving(true);
    const ok = await onSave(draft);
    setSaving(false);
    if (ok) {
      toast.success('נשמר');
      setEditing(false);
    }
  };

  const handleCancel = () => {
    setDraft(override ?? defaultOverride(service));
    setEditing(false);
  };

  const current = draft;
  const wd = current.allowed_weekdays;
  const windows = current.allowed_windows ?? [];

  return (
    <Card className="p-4" dir="rtl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium">{service.title}</div>
            {service.price && <Badge variant="secondary">{service.price}</Badge>}
            {service.duration && <Badge variant="outline">{service.duration}</Badge>}
            <Badge variant={current.bookable ? 'default' : 'destructive'}>
              {current.bookable ? 'זמין להזמנה' : 'לא זמין'}
            </Badge>
          </div>
          {service.subtitle && (
            <div className="text-xs text-muted-foreground">{service.subtitle}</div>
          )}
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? 'שומר…' : 'שמירה'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>
              סגירה
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            עריכה
          </Button>
        )}
      </div>


      {!editing ? (
        <div className="mt-3 grid gap-1 text-sm text-muted-foreground">
          <div>
            <span className="text-foreground">משך: </span>
            {current.duration_minutes} דק׳
          </div>
          <div>
            <span className="text-foreground">ימים מותרים: </span>
            {wd && wd.length ? wd.map((i) => DAYS[i]).join(', ') : 'כל הימים'}
          </div>
          <div>
            <span className="text-foreground">טווחי שעות: </span>
            {windows.length === 0
              ? 'ללא הגבלה'
              : windows
                  .map(
                    (w) =>
                      `${DAYS[w.weekday ?? 0]} ${KIND_LABEL[w.kind ?? 'working']} ${w.start}–${w.end}`,
                  )
                  .join(' · ')}
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={draft.bookable}
                onCheckedChange={(v) => setDraft({ ...draft, bookable: v })}
              />
              <Label className="text-xs">זמין להזמנה</Label>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">משך (דק׳)</Label>
              <Input
                type="number"
                value={draft.duration_minutes}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    duration_minutes: Number(e.target.value) || 60,
                  })
                }
                className="w-20 h-8"
              />
            </div>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">
              ימים מותרים (בלי בחירה = כל הימים)
            </div>
            <div className="flex flex-wrap gap-1">
              {DAYS.map((d, i) => {
                const active = draft.allowed_weekdays?.includes(i) ?? false;
                return (
                  <button
                    key={i}
                    onClick={() => {
                      const set = new Set(draft.allowed_weekdays ?? []);
                      if (set.has(i)) set.delete(i);
                      else set.add(i);
                      const arr = Array.from(set).sort();
                      setDraft({
                        ...draft,
                        allowed_weekdays: arr.length ? arr : null,
                      });
                    }}
                    className={`px-2 py-1 text-xs rounded border ${
                      active
                        ? 'bg-neutral-900 text-white border-neutral-900'
                        : 'bg-white'
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pt-1">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              כללי הזמנה (דריסה גלובלית)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <OverrideNumField
                label="מרווח בין טיפולים (דק׳)"
                value={draft.buffer_minutes}
                fallback={defaults.buffer_minutes}
                onChange={(v) => setDraft({ ...draft, buffer_minutes: v })}
              />
              <OverrideNumField
                label="צעד סלוט (דק׳)"
                value={draft.slot_step_minutes}
                fallback={defaults.slot_step_minutes}
                onChange={(v) => setDraft({ ...draft, slot_step_minutes: v })}
              />
              <OverrideNumField
                label="זמן התראה מינ׳ (דק׳)"
                value={draft.min_lead_minutes}
                fallback={defaults.min_lead_minutes}
                onChange={(v) => setDraft({ ...draft, min_lead_minutes: v })}
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              ערך ריק = שימוש בערך הגלובלי מ"הגדרות יומן". ערך כתוב כאן דורס את הגלובלי.
            </p>
          </div>

          <WindowsEditor
            value={draft.allowed_windows ?? []}
            onChange={(v) =>
              setDraft({ ...draft, allowed_windows: v.length ? v : null })
            }
          />


        </div>
      )}
    </Card>
  );
}

function WindowsEditor({
  value,
  onChange,
}: {
  value: Window[];
  onChange: (v: Window[]) => void;
}) {
  const addWindow = (day: number) => {
    onChange([
      ...value,
      { weekday: day, kind: 'working', start: '09:00', end: '13:00' },
    ]);
  };

  const updateWindow = (index: number, patch: Partial<Window>) => {
    onChange(value.map((w, i) => (i === index ? { ...w, ...patch } : w)));
  };

  const removeWindow = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const indexed = value.map((w, i) => ({ w, i }));

  return (
    <div className="mt-2 space-y-2">
      <div className="text-xs text-muted-foreground">
        טווחי שעות לפי יום — שעות עבודה, הפסקות או חריגים. ימים ריקים = ללא הגבלה.
      </div>
      <div className="space-y-2">
        {DAYS.map((dayLabel, day) => {
          const items = indexed.filter(({ w }) => (w.weekday ?? 0) === day);
          return (
            <div key={day} className="border rounded-md p-2">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">יום {dayLabel}</div>
                <Button size="sm" variant="outline" onClick={() => addWindow(day)}>
                  הוספת טווח
                </Button>
              </div>
              {items.length === 0 ? (
                <div className="text-xs text-muted-foreground">—</div>
              ) : (
                <div className="space-y-1.5">
                  {items.map(({ w, i }) => (
                    <div
                      key={i}
                      className={`flex flex-wrap items-center gap-2 rounded border px-2 py-1.5 ${KIND_STYLE[w.kind ?? 'working']}`}
                    >
                      <select
                        value={w.kind ?? 'working'}
                        onChange={(e) =>
                          updateWindow(i, { kind: e.target.value as WindowKind })
                        }
                        className="h-8 rounded border bg-white px-2 text-xs"
                      >
                        {(Object.keys(KIND_LABEL) as WindowKind[]).map((k) => (
                          <option key={k} value={k}>
                            {KIND_LABEL[k]}
                          </option>
                        ))}
                      </select>
                      <Input
                        type="time"
                        value={w.start}
                        className="w-28 h-8"
                        onChange={(e) => updateWindow(i, { start: e.target.value })}
                      />
                      <span>–</span>
                      <Input
                        type="time"
                        value={w.end}
                        className="w-28 h-8"
                        onChange={(e) => updateWindow(i, { end: e.target.value })}
                      />
                      <Input
                        placeholder="הערה (לא חובה)"
                        value={w.note ?? ''}
                        className="h-8 flex-1 min-w-[140px]"
                        onChange={(e) => updateWindow(i, { note: e.target.value })}
                      />
                      <Button size="sm" variant="ghost" onClick={() => removeWindow(i)}>
                        הסרה
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverrideNumField({
  label,
  value,
  fallback,
  onChange,
}: {
  label: string;
  value: number | null;
  fallback: number;
  onChange: (v: number | null) => void;
}) {
  const isOverridden = value !== null;
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        {isOverridden && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-[10px] text-muted-foreground underline hover:text-foreground"
          >
            החזר לגלובלי
          </button>
        )}
      </div>
      <Input
        type="number"
        value={value ?? ''}
        placeholder={`גלובלי: ${fallback}`}
        onChange={(e) => {
          const raw = e.target.value.trim();
          if (raw === '') onChange(null);
          else {
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : null);
          }
        }}
      />
    </div>
  );
}
